import { useState, useEffect } from 'react';
import { getRandomSponsors } from '../services/sponsorService';

const SponsorCarousel = ({ count = 3, interval = 5000, className = '' }) => {
  const [sponsors, setSponsors] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const loadSponsors = async () => {
    setLoading(true);
    const result = await getRandomSponsors(count);
    if (result.success && result.data.length > 0) {
      setSponsors(result.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSponsors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  useEffect(() => {
    if (sponsors.length <= 1) return;

    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % sponsors.length);
        setIsTransitioning(false);
      }, 300);
    }, interval);

    return () => clearInterval(timer);
  }, [sponsors, interval]);

  if (loading) {
    return (
      <div className={`bg-gradient-to-r from-gray-100 to-gray-200 animate-pulse rounded-xl shadow-lg ${className}`}>
        <div className="w-full h-56 sm:h-64 md:h-72 lg:h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"></div>
            <span className="text-gray-500 text-xs sm:text-sm font-medium">Carregando patrocínios...</span>
          </div>
        </div>
      </div>
    );
  }

  if (sponsors.length === 0) {
    return null;
  }

  const currentSponsor = sponsors[currentIndex];

  const handleClick = () => {
    if (currentSponsor.url) {
      window.open(currentSponsor.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDotClick = (index) => {
    if (index !== currentIndex) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(index);
        setIsTransitioning(false);
      }, 300);
    }
  };

  const goToPrevious = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + sponsors.length) % sponsors.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % sponsors.length);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <div className={`relative group ${className}`}>
      {/* Label de patrocinado */}
      <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white text-[10px] sm:text-xs font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full z-10 shadow-lg flex items-center gap-0.5 sm:gap-1">
        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        <span className="hidden xs:inline">PATROCINADO</span>
        <span className="xs:hidden">PATROC.</span>
      </div>

      {/* Container do banner */}
      <div className="relative overflow-hidden rounded-xl shadow-2xl">
        {/* Banner atual */}
        <div
          onClick={handleClick}
          className={`relative transition-all duration-300 ${
            currentSponsor.url ? 'cursor-pointer' : ''
          } ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
        >
          <div className="relative w-full h-56 sm:h-64 md:h-72 lg:h-80 bg-gradient-to-br from-gray-800 to-gray-900">
            <img
              src={currentSponsor.imageUrl}
              alt={currentSponsor.name}
              className="w-full h-full object-cover"
            />
            {/* Overlay gradient no hover */}
            {currentSponsor.url && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            )}
          </div>
          
          {/* Info overlay (bottom) */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-3 sm:p-4 md:p-6">
            <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-1 line-clamp-1">{currentSponsor.name}</h3>
            {currentSponsor.description && (
              <p className="text-white/90 text-xs sm:text-sm md:text-base line-clamp-2">
                {currentSponsor.description}
              </p>
            )}
            {currentSponsor.url && (
              <div className="mt-2 flex items-center text-white/80 text-xs sm:text-sm font-medium group-hover:text-white transition-colors">
                <span>Saiba mais</span>
                <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Botões de navegação (visíveis apenas no hover em desktop, escondidos no mobile) */}
        {sponsors.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="hidden md:flex absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 md:p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 items-center justify-center"
              aria-label="Patrocínio anterior"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="hidden md:flex absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 md:p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 items-center justify-center"
              aria-label="Próximo patrocínio"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Indicadores (dots) - agora mais visíveis */}
      {sponsors.length > 1 && (
        <div className="flex justify-center items-center gap-1.5 sm:gap-2 mt-3 sm:mt-4">
          {sponsors.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? 'bg-blue-600 w-6 sm:w-8 h-2 sm:h-2.5 shadow-md'
                  : 'bg-gray-300 hover:bg-gray-400 w-2 sm:w-2.5 h-2 sm:h-2.5'
              }`}
              aria-label={`Ir para patrocínio ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Contador - agora mais discreto */}
      {sponsors.length > 1 && (
        <div className="text-center mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-400 font-medium">
          {currentIndex + 1} / {sponsors.length}
        </div>
      )}
    </div>
  );
};

export default SponsorCarousel;

